/**
 * FreeFormSahaListPage.jsx — Bölüm 25-I
 * Saha/Denetçi tarafı: Doldurulabilir esnek form şablonlarını listeler.
 * Route: /FdsSaha/FreeForm
 *
 * Akış:
 *  1. Tüm şablonlar listelenir (targetEntityType filtresiyle)
 *  2. Şablona tıklanınca → /FdsSaha/FreeForm/:templateId
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge, Button, Card, CardBody,
  Col, Input, Row, Spinner,
} from "reactstrap";
import {
  ChevronRight, FolderOpen, Search, FileText,
} from "lucide-react";
import { PageFrame, PageBody } from "../../Components/Common/EmbeddablePage";
import { listTemplates } from "../../services/freeFormService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// ─── Sabitler ────────────────────────────────────────────────────────────────

const ENTITY_LABELS = { get FAIR() { return i18n.t("saha:freeFormSahaList.fuar"); }, get SALON() { return i18n.t("saha:freeFormSahaList.salon"); }, get STAND() { return i18n.t("saha:freeFormSahaList.stant"); }, get PERSON() { return i18n.t("saha:freeFormSahaList.kisi"); } };
const ENTITY_COLORS = { FAIR: "primary", SALON: "info", STAND: "success", PERSON: "warning" };

const ENTITY_FILTERS = [
  { value: "",        get label() { return i18n.t("common:tumu"); } },
  { value: "FAIR",    get label() { return i18n.t("saha:freeFormSahaList.fuar"); } },
  { value: "SALON",   get label() { return i18n.t("saha:freeFormSahaList.salon"); } },
  { value: "STAND",   get label() { return i18n.t("saha:freeFormSahaList.stant"); } },
  { value: "PERSON",  get label() { return i18n.t("saha:freeFormSahaList.kisi"); } },
];

const LIMIT = 50;

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────

// embedded: boolean true → sayfa kabuğu kapanır (sekme olarak render edilir)
export default function FreeFormSahaListPage({ embedded = false }) {
  const { t: tx } = useTranslation("saha");
  const navigate = useNavigate();

  const [templates,   setTemplates]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [filterType,  setFilterType]  = useState("");
  const [search,      setSearch]      = useState("");
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);

  useEffect(() => {
    document.title = i18n.t("saha:freeFormSahaList.esnekFormDoldurFds");
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: page - 1, limit: LIMIT };
      if (filterType) params.targetEntityType = filterType;
      const data = await listTemplates(params);
      const list = Array.isArray(data)
        ? data
        : (data?.records || data?.content || data?.templates || data?.data?.content || []);
      const pages = data?.totalPages || Math.ceil((data?.total || 0) / LIMIT) || 1;
      setTemplates(list);
      setTotalPages(pages);
    } catch (err) {
      setError(err?.response?.data?.message || i18n.t("saha:freeFormSahaList.sablonlarYuklenemedi"));
    } finally {
      setLoading(false);
    }
  }, [page, filterType]);

  useEffect(() => {
    async function run() { await load(); }
    run();
  }, [load]);

  // Client-side arama
  const filtered = search.trim()
    ? templates.filter((t) =>
        t.name?.toLowerCase().includes(search.toLowerCase()) ||
        t.description?.toLowerCase().includes(search.toLowerCase())
      )
    : templates;

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={tx("freeFormSahaList.esnekFormDoldur")} pageTitle={tx("freeFormSahaList.fdsSaha")}>

        {/* ── Filtre + Arama ────────────────────────────────────────────── */}
        <Card className="border-0 shadow-sm mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} sm={5} md={4}>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <Search size={13} />
                  </span>
                  <Input
                    bsSize="sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={tx("freeFormSahaList.formAdiAra")}
                  />
                </div>
              </Col>
              <Col xs={12} sm={4} md={3}>
                <Input
                  type="select"
                  bsSize="sm"
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                >
                  {ENTITY_FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </Input>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ── Liste ─────────────────────────────────────────────────────── */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2 small">{tx("freeFormSahaList.sablonlarYukleniyor")}</p>
          </div>
        ) : error ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-4 text-danger small">{error}</CardBody>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-5">
              <FolderOpen size={40} className="text-muted mb-2" />
              <p className="text-muted small mb-0">{tx("freeFormSahaList.formSablonuBulunamadi")}</p>
            </CardBody>
          </Card>
        ) : (
          <Row className="g-3">
            {filtered.map((t) => {
              const tId = t.id || t._id;
              const isLocked = (t.submissionCount || 0) > 0;
              return (
                <Col key={tId} xs={12} sm={6} md={4} lg={3}>
                  <Card
                    className="border-0 shadow-sm h-100"
                    style={{ cursor: "pointer", transition: "box-shadow .15s" }}
                    onClick={() => navigate(`/FdsSaha/FreeForm/${tId}`)}
                  >
                    <CardBody className="d-flex flex-column gap-2 p-3">
                      {/* Başlık */}
                      <div className="d-flex align-items-start gap-2">
                        <FileText size={18} className="text-primary flex-shrink-0 mt-1" />
                        <span className="fw-semibold small flex-grow-1">{t.name}</span>
                      </div>

                      {/* Açıklama */}
                      {t.description && (
                        <p
                          className="text-muted small mb-0"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {t.description}
                        </p>
                      )}

                      {/* Alt bilgi */}
                      <div className="d-flex align-items-center justify-content-between mt-auto pt-1 border-top">
                        <Badge
                          color={ENTITY_COLORS[t.targetEntityType] || "secondary"}
                          pill
                          style={{ fontSize: "0.65rem" }}
                        >
                          {ENTITY_LABELS[t.targetEntityType] || t.targetEntityType || "—"}
                        </Badge>
                        <div className="d-flex align-items-center gap-1">
                          {isLocked && (
                            <span className="text-muted" style={{ fontSize: "0.65rem" }}>
                              {tx("freeFormSahaList.doldurulmus", { submissionCount: t.submissionCount })}
                            </span>
                          )}
                          <ChevronRight size={14} className="text-muted" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* ── Sayfalama ─────────────────────────────────────────────────── */}
        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-center gap-2 mt-3">
            <Button
              size="sm" color="light"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {tx("freeFormSahaList.onceki")}
            </Button>
            <span className="d-flex align-items-center small text-muted px-2">
              {page} / {totalPages}
            </span>
            <Button
              size="sm" color="light"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {tx("freeFormSahaList.sonraki")}
            </Button>
          </div>
        )}
      </PageBody>
    </PageFrame>
  );
}


